import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import {
  FolderOutlined,
  PlayCircleOutlined,
  LogoutOutlined,
  BorderOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TrophyOutlined,
  SwapOutlined,
  RobotOutlined,
  ReadOutlined,
  SettingOutlined,
  AppstoreOutlined,
  FundOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from 'antd';
import { usePortfolios } from '../../../hooks/usePortfolios';
import { useSidebar } from '../../../contexts/SidebarContext';
import {
  SidebarSupportEntry,
  SidebarSupportRailButton,
} from '../../../components/SupportWidget/SidebarSupportEntry';
import {
  SidebarContainer,
  SidebarHeader,
  SidebarTitle,
  SidebarSection,
  SidebarNavItem,
  StatValue,
} from '../styles';

/** Every sidebar destination, in order; the collapsed rail shows the same list as icons. */
const NAV_ITEMS: { path: string; label: string; icon: React.ReactNode }[] = [
  { path: '/home', label: 'Strategies', icon: <AppstoreOutlined /> },
  { path: '/portfolio', label: 'Portfolio', icon: <FolderOutlined /> },
  { path: '/grid-trading', label: 'Grid Trading', icon: <BorderOutlined /> },
  { path: '/pair-trading', label: 'Pair Trading', icon: <SwapOutlined /> },
  { path: '/smart-beta', label: 'Smart Beta', icon: <PieChartOutlined /> },
  { path: '/price-board', label: 'Price Board', icon: <FundOutlined /> },
  { path: '/executions', label: 'Executions', icon: <PlayCircleOutlined /> },
  { path: '/tournament', label: 'Tournament', icon: <TrophyOutlined /> },
  { path: '/ai', label: 'AI Assistant', icon: <RobotOutlined /> },
  { path: '/docs', label: 'Docs', icon: <ReadOutlined /> },
];

const Sidebar: React.FC = () => {
  const { collapsed, toggle: onToggle } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { portfolios, loading } = usePortfolios();
  const activePortfolioCount = portfolios.length;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (collapsed) {
    return (
      <div style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '48px',
        height: '100vh',
        background: '#1A1D23',
        borderRight: '1px solid #2B3139',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '12px',
        zIndex: 100,
        gap: '16px'
      }}>
        <Button
          type="text"
          icon={<MenuUnfoldOutlined />}
          onClick={onToggle}
          style={{ color: '#848E9C', fontSize: '16px', width: '36px', height: '36px' }}
        />
        {[...NAV_ITEMS, { path: '/settings', label: 'Settings', icon: <SettingOutlined /> }].map(({ path, label, icon }) => (
          <Button key={path} type="text" icon={icon} onClick={() => navigate(path)} title={label}
            style={{ color: location.pathname === path ? '#0ECB81' : '#848E9C', width: '36px', height: '36px' }} />
        ))}
        <div style={{ marginTop: 'auto', paddingBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <SidebarSupportRailButton />
          <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}
            style={{ color: '#848E9C', width: '36px', height: '36px' }} />
        </div>
      </div>
    );
  }

  return (
    <SidebarContainer>
      <SidebarHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SidebarTitle>Dashboard</SidebarTitle>
        <Button
          type="text"
          icon={<MenuFoldOutlined />}
          onClick={onToggle}
          size="small"
          style={{ color: '#848E9C' }}
        />
      </SidebarHeader>

      {NAV_ITEMS.map(({ path, label, icon }) => (
        <SidebarNavItem key={path} $active={location.pathname === path} onClick={() => navigate(path)}>
          {icon}
          <span className="nav-label">{label}</span>
          {path === '/portfolio' && (loading ? <Spin size="small" /> : <StatValue>{activePortfolioCount}</StatValue>)}
        </SidebarNavItem>
      ))}

      <SidebarSupportEntry />

      <SidebarNavItem $active={location.pathname === '/settings'} onClick={() => navigate('/settings')}>
        <SettingOutlined />
        <span className="nav-label">Settings</span>
      </SidebarNavItem>

      <SidebarSection style={{ marginTop: 'auto', paddingTop: '24px', borderTop: '1px solid #2B3139' }}>
        <Button
          type="default"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          block
          style={{
            height: '40px',
            borderColor: '#2B3139',
            color: '#848E9C',
            background: 'transparent',
            fontWeight: 500,
            boxShadow: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0ECB81';
            e.currentTarget.style.color = '#0ECB81';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#2B3139';
            e.currentTarget.style.color = '#848E9C';
          }}
        >
          Logout
        </Button>
      </SidebarSection>
    </SidebarContainer>
  );
};

export default Sidebar;

